import {
  type Badge,
  cn,
  Dialog,
  DialogBackdrop,
  DialogBody,
  DialogPopup,
  DialogPortal,
  DialogRoot,
  Switch,
  Text,
  VersionHistoryAccordion,
} from '@risksmart-app/atomic-ui';
import { type ComponentProps, type ReactNode, useState } from 'react';
import { toLocalDateTime } from 'src/utils/dateUtils';

import { MatrixPreview, RatingsPreview } from '../risk-scoring-config-preview';
import type {
  RiskScoringVersionHistoryProps,
  VersionHistoryEntry,
} from './types';

const RiskScoringVersionHistory = ({
  lang,
  state,
  actions,
}: RiskScoringVersionHistoryProps) => {
  const { open, versions } = state;
  const { onOpenChange } = actions;

  const latestId = versions.find((entry) => entry.isLatest)?.id;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogPopup size={'lg'} className={cn('w-fit max-w-[90vw]')}>
          <Dialog.Header
            title={lang.title}
            description={lang.description}
          ></Dialog.Header>
          <DialogBody
            className={cn('w-fit max-w-[90vw] max-h-[80vh] overflow-auto')}
          >
            {/* Invisible width-setter: keeps the dialog pinned to the widest
                version's preview in every collapsed/expanded state. It renders
                each preview in its widest (compare-expanded) form via
                forceCompare, contributes width (w-max) but no height (h-0), and
                is inert so its duplicate switches stay out of the tab order and
                the a11y tree. The p-4 mirrors the accordion panel padding so the
                measured width matches an expanded panel exactly. */}
            <div
              aria-hidden
              inert
              className={cn('pointer-events-none h-0 w-max overflow-hidden')}
            >
              {versions.map((entry) => (
                <div key={entry.id} className={cn('p-4')}>
                  <VersionConfigPreview
                    entry={entry}
                    lang={lang}
                    forceCompare
                  />
                </div>
              ))}
            </div>
            {/* base-ui Accordion is single-open by default (multiple={false});
                we only need to open the latest version on mount. */}
            <VersionHistoryAccordion
              defaultValue={latestId !== undefined ? [latestId] : []}
            >
              {versions.map((entry) => (
                <VersionHistoryEntryItem
                  key={entry.id}
                  entry={entry}
                  lang={lang}
                />
              ))}
            </VersionHistoryAccordion>
          </DialogBody>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  );
};

interface VersionConfigPreviewProps {
  entry: VersionHistoryEntry;
  lang: RiskScoringVersionHistoryProps['lang'];
  // Measurement-only: render the widest (compare-expanded) layout so the hidden
  // width-setter pins the dialog to that state even while the visible copy
  // starts collapsed. Never set on the visible (interactive) copy.
  forceCompare?: boolean;
}

const VersionConfigPreview = ({
  entry,
  lang,
  forceCompare = false,
}: VersionConfigPreviewProps) => {
  const [compare, setCompare] = useState(false);
  const timestamp = toLocalDateTime(entry.updatedAt ?? entry.createdAt);

  const { config, initialConfig } = entry;

  const variants =
    (compare || forceCompare) && initialConfig !== undefined
      ? [
          { key: 'current', config, label: lang.currentConfiguration },
          {
            key: 'initial',
            config: initialConfig,
            label: lang.initialConfiguration,
          },
        ]
      : [{ key: 'current', config, label: undefined }];

  return (
    <div className={cn('flex flex-col gap-4')}>
      <div className={cn('flex flex-col gap-1')}>
        <Text preset={'body-md'} className={cn('font-semibold')}>
          {lang.fullyEffective}
        </Text>
        <Text preset={'body-md'}>{timestamp}</Text>
      </div>
      {initialConfig !== undefined && (
        <div className={cn('flex items-center gap-2')}>
          <Text preset={'body-md'} className={cn('font-semibold')}>
            {lang.compareChanges}
          </Text>
          <Switch
            checked={compare}
            onCheckedChange={setCompare}
            aria-label={lang.compareChanges}
          />
        </div>
      )}
      <div className={cn('flex flex-col gap-2')}>
        {variants.map((variant) => (
          <ConfigPreviewBox key={variant.key} label={variant.label}>
            <RatingsPreview
              config={variant.config}
              likelihoodLabel={lang.likelihoodLabel}
              impactLabel={lang.impactLabel}
            />
          </ConfigPreviewBox>
        ))}
        <div className={cn('-mx-4 my-1 border-t border-neutral-border')} />
        {variants.map((variant) => (
          <ConfigPreviewBox key={variant.key} label={variant.label}>
            <MatrixPreview
              config={variant.config}
              likelihoodAxisLabel={lang.likelihoodAxisLabel}
              impactAxisLabel={lang.impactAxisLabel}
            />
          </ConfigPreviewBox>
        ))}
      </div>
    </div>
  );
};

interface ConfigPreviewBoxProps {
  label?: string;
  children: ReactNode;
}

const ConfigPreviewBox = ({ label, children }: ConfigPreviewBoxProps) => (
  <div className={cn('flex flex-col gap-2')}>
    {label !== undefined && (
      <Text preset={'body-md'} className={cn('font-semibold')}>
        {label}
      </Text>
    )}
    <div
      className={cn('rounded-lg border border-neutral-border bg-neutral p-4')}
    >
      {children}
    </div>
  </div>
);

interface VersionHistoryEntryItemProps {
  entry: VersionHistoryEntry;
  lang: RiskScoringVersionHistoryProps['lang'];
}

const VersionHistoryEntryItem = ({
  entry,
  lang,
}: VersionHistoryEntryItemProps) => {
  const timestamp = toLocalDateTime(entry.createdAt);

  const badges: ComponentProps<typeof Badge>[] = [
    ...(entry.isUpdated
      ? [
          {
            children: lang.updatedBadge,
            variant: 'warning' as const,
            border: false,
          },
        ]
      : []),
    entry.isLatest
      ? {
          children: lang.currentBadge,
          variant: 'secondary' as const,
          border: false,
        }
      : {
          children: lang.archivedBadge,
          variant: 'muted' as const,
          border: false,
        },
    {
      children: lang.methodologyBadge[entry.methodology],
      variant: 'neutral' as const,
      border: false,
    },
  ];

  return (
    <VersionHistoryAccordion.Item value={entry.id} isActive={entry.isLatest}>
      <VersionHistoryAccordion.Trigger
        title={`${lang.versionLabel} ${entry.version}`}
        badges={badges}
        user={entry.createdByName}
        timestamp={timestamp}
      />
      <VersionHistoryAccordion.Content>
        <VersionConfigPreview entry={entry} lang={lang} />
      </VersionHistoryAccordion.Content>
    </VersionHistoryAccordion.Item>
  );
};

export { RiskScoringVersionHistory };
export type {
  RiskScoringVersionHistoryActions,
  RiskScoringVersionHistoryLang,
  RiskScoringVersionHistoryProps,
  RiskScoringVersionHistoryState,
  ScoringMethodology,
  VersionHistoryEntry,
} from './types';
